import React, { useState } from 'react';
import {
  Autocomplete,
  AutocompleteProps,
  Box,
  Divider,
  FormControl,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  MenuItemProps,
  TextField,
  Tooltip,
} from '@mui/material';
import { SxProps } from '@mui/system';
import { AutocompleteDropdown, SelectItem } from '@askporter/utils';
import { Drawer, Select, SelectProps } from '../../';
import { renderOption } from './utils';

interface CommonProps {
  /*
   * @param idPrefix - prefix for all id's and labels, includes data-testid
   * */
  idPrefix: string;
  /*
   * @param isSmallDevice - true if small viewport
   */
  isSmallDevice: boolean;
  /**
   * @param wrapperStyles - optional style overrides for the box which wraps the entire component
   */
  wrapperStyles?: SxProps;
  /**
   * @param startAdornment - optional start adornment for the select dropdown
   */
  startAdornment?: SelectProps['startAdornment'];
  /**
   * @param readOnly - optional flag to make the component read-only
   */
  readOnly?: boolean;
  /**
   * @param readOnlyText - optional tooltip title when readOnly is true
   */
  readOnlyText?: string;
  /**
   * @param disableMargin - optional flag to disable the margin on the component
   */
  disableMargin?: boolean;
  /**
   * @param inlineLabel - optional flag to make the component inline label
   */
  inlineLabel?: boolean;
  /**
   * @param label - optional label for the component
   */
  label?: string;
  /**
   * @param leftSlot - optional left slot for the component
   */
  leftSlot?: React.ReactNode;
  /**
   * @param size - size of the component
   */
  size?: SelectProps['size'];
}

type AutoCompProps = Omit<
  AutocompleteProps<SelectItem<MenuItemProps['value']>, false, boolean, undefined, 'div'>,
  'renderInput' | 'id' | 'data-testid' | 'inputValue' | 'onInputChange' | 'onChange'
>;

export interface ResponsiveSelectDropdownDrawer<T> {
  items: Array<SelectItem<T> & { disabled?: boolean; divider?: boolean; sx?: Record<string, unknown> }>;
  onSelect: (value: string | number | readonly string[], label?: string) => void;
  value: T;
  label: string;
}

export type StatusSelectorProps =
  | ({
      mode: 'select';
      selectDropdown: ResponsiveSelectDropdownDrawer<MenuItemProps['value']>;
    } & CommonProps)
  | ({
      mode: 'autocomplete';
      selectDropdown: AutocompleteDropdown;
    } & CommonProps &
      AutoCompProps);

/**
 * Renders a ResponsiveSelectDrawer component
 */
export const ResponsiveSelectDrawer: React.FC<React.PropsWithChildren<StatusSelectorProps>> = ({
  selectDropdown,
  idPrefix,
  isSmallDevice,
  wrapperStyles,
  startAdornment,
  mode = 'select',
  readOnly = false,
  readOnlyText = undefined,
  disableMargin = false,
  inlineLabel = false,
  size = 'medium',
  leftSlot = undefined,
  ...autocompleteProps
}: StatusSelectorProps) => {
  const [standardComponentOpen, setStandardComponentOpen] = useState(false);
  const [drawer, setDrawer] = useState(false); // mobile drawer visibility
  const [inputValue, setInputValue] = React.useState(''); // autocomplete search value

  const renderComponents = () => {
    const components = { mainComponent: <></>, mobileOpenComponent: <></> };

    if (mode === 'select') {
      const select = selectDropdown as ResponsiveSelectDropdownDrawer<MenuItemProps['value']>;
      // eslint-disable-next-line functional/immutable-data
      components.mainComponent = (
        <Tooltip title={readOnly ? readOnlyText : undefined} placement="top-end">
          <FormControl variant="filled" fullWidth sx={{ m: 0, mb: disableMargin ? 0 : 3 }}>
            <Select
              data-testid={`${idPrefix}-select-component`}
              labelId={`${idPrefix}-label`}
              aria-label={select?.label}
              label={select?.label}
              inlineLabel={inlineLabel}
              size={size}
              id={`${idPrefix}-select`}
              value={select?.value}
              leftSlot={leftSlot}
              onChange={(event) => {
                select?.onSelect(event?.target?.value as string | number | readonly string[]);
              }}
              displayEmpty
              defaultValue=""
              fullWidth
              startAdornment={startAdornment}
              open={standardComponentOpen}
              onClick={isSmallDevice && !readOnly ? () => setDrawer(true) : undefined}
              onOpen={isSmallDevice ? undefined : () => setStandardComponentOpen(true)}
              onClose={() => setStandardComponentOpen(false)}
              readOnly={readOnly}
            >
              {select?.items?.map((option, index) => {
                const hideItem = option.hidden
                  ? {
                      display: 'none',
                    }
                  : {};

                const sx = option.sx ? option.sx : {};

                return option.divider ? (
                  <Divider key={index} />
                ) : (
                  <MenuItem
                    key={`${index}_${option?.value}_${option?.label}`}
                    value={option?.value}
                    data-testid={`${idPrefix}-select-item`}
                    sx={{ ...sx, ...hideItem }}
                    disabled={option.disabled === true}
                  >
                    {option?.label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Tooltip>
      );

      // eslint-disable-next-line functional/immutable-data
      components.mobileOpenComponent = (
        <Drawer open={drawer} onClose={setDrawer} title={selectDropdown?.label} dataTestId={`${idPrefix}-drawer`}>
          <List data-testid={`${idPrefix}-select-open-mobile`}>
            {select?.items?.map((item, ind: number) => {
              const hideItem = item.hidden
                ? {
                    display: 'none',
                  }
                : {};

              const sx = item.sx ? item.sx : {};

              return item.divider ? (
                <Divider key={ind} />
              ) : (
                <ListItemButton
                  key={ind}
                  alignItems="flex-start"
                  onClick={() => {
                    if (item?.value !== undefined) {
                      select?.onSelect(item.value);
                    }
                    setDrawer(false);
                  }}
                  dense={true}
                  selected={item?.value === select?.value}
                  data-testid={`${idPrefix}-select-item`}
                  sx={{ ...sx, ...hideItem }}
                  disabled={item.disabled === true}
                >
                  <ListItemText primary={item?.label} />
                  {item.divider ? <Divider /> : null}
                </ListItemButton>
              );
            })}
          </List>
        </Drawer>
      );
    }

    if (mode === 'autocomplete') {
      const autocompleteDropdown = selectDropdown as AutocompleteDropdown;
      const { options, ...autocomplete } = autocompleteProps as AutoCompProps;

      const commonAutocompleteProps = {
        defaultValue: null as SelectItem<MenuItemProps['value']> | null,
        options: options,
        'data-testid': `${idPrefix}-autocomplete-component`,
        id: `${idPrefix}-autocomplete`,
        open: standardComponentOpen,
        onOpen: isSmallDevice ? undefined : () => setStandardComponentOpen(true),
        onClose: () => setStandardComponentOpen(false),
        inputValue: inputValue,
        onInputChange: (_: React.SyntheticEvent, newInputValue: string) => {
          setInputValue(newInputValue);
        },
        onChange: (_: React.SyntheticEvent, newValue: SelectItem<MenuItemProps['value']> | null) => {
          isSmallDevice && setDrawer(false);
          if (newValue) {
            autocompleteDropdown?.onChange(newValue.value as string);
          }
        },
        renderOption,
      };

      // eslint-disable-next-line functional/immutable-data
      components.mainComponent = (
        <Autocomplete
          {...autocomplete}
          {...commonAutocompleteProps}
          renderInput={(params) => (
            <TextField
              {...params}
              onClick={isSmallDevice ? () => setDrawer(true) : () => setStandardComponentOpen(true)}
              onKeyPress={isSmallDevice ? () => setDrawer(true) : undefined}
              variant="filled"
              label={autocompleteDropdown?.label}
              InputProps={{
                ...params.InputProps,
                startAdornment,
              }}
            />
          )}
        />
      );

      // eslint-disable-next-line functional/immutable-data
      components.mobileOpenComponent = (
        <Drawer
          open={drawer}
          onClose={setDrawer}
          title={selectDropdown?.label}
          dataTestId={`${idPrefix}-drawer`}
          sx={{
            '& .MuiDrawer-paperAnchorBottom': {
              position: 'fixed',
              top: 0,

              '& .MuiAutocomplete-listbox': {
                maxHeight: 'none',
              },
            },
          }}
        >
          <Autocomplete
            {...autocomplete}
            {...commonAutocompleteProps}
            disableCloseOnSelect
            disablePortal
            open={true}
            renderInput={(params) => (
              <InputBase
                ref={params.InputProps.ref}
                inputProps={params.inputProps}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                sx={{
                  width: '100%',
                  '& input': {
                    borderRadius: 2,
                    backgroundColor: 'white',
                    padding: 2,
                    border: '1px solid #ced4da',
                    '&:focus': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            )}
          />
        </Drawer>
      );
    }

    return components;
  };

  const components = renderComponents();

  return (
    <Box data-testid={`${idPrefix}-responsive-select`} width="100%" sx={{ ...wrapperStyles }}>
      {components.mainComponent}
      {isSmallDevice && components.mobileOpenComponent}
    </Box>
  );
};
